import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { commandCopy } from "components/commandPalette/commandBuilders";
import { useRegisterCommands } from "components/CommandPaletteRegistry";
import { Dialog } from "components/Dialog";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { FormAutocomplete } from "components/Forms/FormAutocomplete";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { ToggleField } from "components/Forms/ToggleField";
import { LepcPickerOmnisearch } from "components/LepcPicker";
import { FormTextFieldSkeleton } from "components/Skeleton";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { safeParseInt } from "encamp-shared/src/tierii/utilities";
import {
  ChemicalDbRequirement,
  LepcPickerFragment,
  PortalInput,
  PortalType,
  ProgramArea,
} from "generated-graphql/graphql";
import { sortBy } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { GET_LEPC } from "routes/Staff/Agencies/Lepcs/api";
import { parseLepcJurisdiction } from "util/portal";

import { GET_PORTAL, UPSERT_PORTAL } from "./api";

type PortalDialogProps = {
  onClose: () => void;
  mode?: "add" | "edit";
  portalId?: string;
};

const PORTAL_TYPE_OPTIONS = [
  { value: PortalType.State, display: "State" },
  { value: PortalType.Lepc, display: "LEPC" },
  { value: PortalType.City, display: "City" },
  { value: PortalType.Federal, display: "Federal" },
];

const STATE_SELECT_OPTIONS: { value: string; display: string }[] = sortBy(
  Object.entries(STATE_NAMES),
  1
).map(([abbr, name]) => ({ value: abbr, display: `${name} (${abbr})` }));

const CHEMICAL_DB_OPTIONS = [
  { value: ChemicalDbRequirement.No, display: "No" },
  { value: ChemicalDbRequirement.EhsOnly, display: "EHS Only" },
  { value: ChemicalDbRequirement.All, display: "All" },
];

const PROGRAM_AREA_OPTIONS = [
  { value: ProgramArea.Epcra, display: "EPCRA" },
  { value: ProgramArea.Rcra, display: "RCRA" },
];

/** Suggested portal tech options; freeSolo allows any custom value. */
const PORTAL_TECH_OPTIONS = [
  { value: "EPlan", label: "ePlan" },
  { value: "Tier2Manager", label: "TierIIManager" },
];

/** Min height to match FormControl (label + outlined input + FormHelperText) so skeleton doesn't jump when form loads. */
const FORM_FIELD_MIN_HEIGHT = 96;

function PortalFormSkeleton() {
  const theme = useTheme();
  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Grid container spacing={3}>
        {/* Description (8) and Type (4) - first row */}
        <Grid item xs={12} md={8}>
          <Box sx={{ minHeight: 100 }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        {/* Jurisdiction / picker row */}
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        {/* Portal Tech (6) and URL (6) */}
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        {/* Chemical DB (6) and Program Areas (6) */}
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        {/* Login options - match Typography variant="subtitle2" + mb: 1 */}
        <Grid item xs={12}>
          <Skeleton variant="text" width={100} height={20} sx={{ mb: theme.spacing(1) }} />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} md={6} key={i}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Skeleton variant="rounded" width={44} height={24} />
                  <Skeleton variant="text" width={120} height={24} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export const CreateEditPortalDialog: React.FC<PortalDialogProps> = ({
  onClose,
  mode,
  portalId,
}) => {
  const alerts = useAlerts();

  const { data: portalData, loading: isLoadingPortal } = useQuery(GET_PORTAL, {
    fetchPolicy: "cache-and-network",
    variables: { id: portalId ?? "" },
    skip: !portalId,
  });

  const [upsertPortal, { loading: isUpserting }] = useMutation(UPSERT_PORTAL, {
    refetchQueries: ["PortalSearch"],
  });

  const portal = portalData?.portal;

  const defaultValues: PortalInput = useMemo(
    () => ({
      type: portal?.type ?? PortalType.State,
      jurisdiction: portal?.jurisdiction ?? "",
      portalTech: portal?.portalTech ?? "",
      url: portal?.url ?? null,
      capacity: portal?.capacity ?? 10,
      allowsMultiSessionLogin: portal?.allowsMultiSessionLogin ?? false,
      allowsCookieReuse: portal?.allowsCookieReuse ?? false,
      requiresUsageOfChemicalDb: portal?.requiresUsageOfChemicalDb ?? ChemicalDbRequirement.No,
      usesMFA: portal?.usesMFA ?? false,
      supportedProgramAreas: portal?.supportedProgramAreas ?? [ProgramArea.Epcra],
      description: portal?.description ?? null,
      isActive: portal?.isActive ?? true,
      verifiable: portal?.verifiable ?? true,
    }),
    [portal]
  );

  const form = useForm<PortalInput>({
    defaultValues,
    values: defaultValues,
  });

  const { control, getValues, setValue, watch, formState } = form;
  const portalType = watch("type");
  const jurisdiction = watch("jurisdiction");

  const [lepcFilterStateInput, setLepcFilterStateInput] = useState<string>("");
  const [selectedLepcLocal, setSelectedLepcLocal] = useState<LepcPickerFragment | null>(null);

  const clearJurisdictionAndLepcState = useCallback(() => {
    setValue("jurisdiction", "", { shouldDirty: true });
    setLepcFilterStateInput("");
    setSelectedLepcLocal(null);
  }, [setValue]);

  const parsedLepc = parseLepcJurisdiction(portal?.jurisdiction ?? null);
  const parsedJurisdiction = useMemo(() => parseLepcJurisdiction(jurisdiction), [jurisdiction]);
  const lepcFilterState = parsedJurisdiction?.state ?? lepcFilterStateInput;

  const { data: lepcData } = useQuery(GET_LEPC, {
    variables: { id: parsedLepc?.lepcId ?? "" },
    skip: !parsedLepc?.lepcId || mode !== "edit" || portalType !== PortalType.Lepc,
  });

  const loadedLepcMatchesForm =
    Boolean(jurisdiction && parsedJurisdiction?.lepcId) &&
    lepcData?.lepc?.id === parsedJurisdiction?.lepcId;
  const lepcFromLoadedData = loadedLepcMatchesForm
    ? (lepcData?.lepc as LepcPickerFragment | undefined)
    : undefined;
  const selectedLepc = selectedLepcLocal ?? lepcFromLoadedData;

  const handleLepcChange = useCallback(
    (lepc: LepcPickerFragment | null) => {
      setSelectedLepcLocal(lepc);
      if (!lepc) {
        const currentState = parseLepcJurisdiction(getValues("jurisdiction"))?.state;
        if (currentState) {
          setLepcFilterStateInput(currentState);
        }
      }
      setValue("jurisdiction", lepc ? `${lepc.state}_${lepc.id}` : "", {
        shouldDirty: true,
      });
    },
    [getValues, setValue]
  );

  const isJurisdictionFromPicker =
    portalType === PortalType.State || portalType === PortalType.Lepc;

  const onSubmit = async () => {
    const input = getValues();
    const capacity =
      typeof input.capacity === "number"
        ? input.capacity
        : (safeParseInt(String(input.capacity)) ?? 10);
    await upsertPortal({
      variables: {
        id: mode === "edit" ? portalId : null,
        input: {
          ...input,
          capacity,
          jurisdiction: input.jurisdiction,
          portalTech: input.portalTech || null,
        },
      },
      onCompleted: () => {
        onClose();
        alerts.success("Portal saved successfully");
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while saving the portal");
      },
    });
  };

  const savingOrLoading = isLoadingPortal || isUpserting;
  const showEditSkeleton = mode === "edit" && isLoadingPortal && !portal;

  const copyToClipboardWithAlert = useCallback(
    async (text: string) => {
      await navigator.clipboard.writeText(text);
      alerts.success("Copied to clipboard");
    },
    [alerts]
  );

  const portalCopyCommands = useMemo(() => {
    if (mode !== "edit" || !portalId) return [];
    return [
      commandCopy({
        id: "portal-dialog-copy-portal-id",
        idKind: "portal ID",
        value: portalId,
        copy: copyToClipboardWithAlert,
        signature: `portal-id:${portalId}`,
        keywords: ["portal", "id", "copy"],
      }),
    ];
  }, [mode, portalId, copyToClipboardWithAlert]);

  useRegisterCommands(portalCopyCommands, [portalCopyCommands], "portal-dialog");

  return (
    <FormProvider {...form}>
      <Dialog
        open
        onClose={onClose}
        maxWidth="md"
        title={`${mode === "add" ? "Add" : "Edit"} Portal`}
        titleBarActionElement={
          showEditSkeleton ? undefined : (
            <Box sx={{ display: "flex", alignItems: "center", marginTop: 3 }}>
              <ToggleField name="isActive" label="Active" control={control} sx={{ margin: 0 }} />
            </Box>
          )
        }
        primaryActionButtonProps={{
          loading: savingOrLoading,
          onClick: onSubmit,
          disabled: !formState.isDirty,
        }}
      >
        {showEditSkeleton ? (
          <PortalFormSkeleton />
        ) : (
          <Box
            component="form"
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              width: "100%",
            }}
          >
            <Grid container spacing={3}>
              {/* First row: Description (8) and Portal Type (4) */}
              <Grid item xs={12} md={8}>
                <FormTextField
                  name="description"
                  label="Description"
                  control={control}
                  textFieldProps={{ multiline: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="portal-type-label">Type</InputLabel>
                      <Select
                        {...field}
                        labelId="portal-type-label"
                        label="Type"
                        onChange={(e) => {
                          const newType = e.target.value as PortalType;
                          if (newType !== field.value) {
                            clearJurisdictionAndLepcState();
                          }
                          field.onChange(newType);
                        }}
                      >
                        {PORTAL_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.display}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Second row: picker(s) + Jurisdiction (flow depends on type) */}
              {portalType === PortalType.State && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormSelect
                      name="jurisdiction"
                      label="State"
                      control={control}
                      selectItems={STATE_SELECT_OPTIONS}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DisplayOnlyField
                      label="Jurisdiction"
                      text={jurisdiction ?? ""}
                      infoTooltip="This is the actual value that we use to identify the portal in the system."
                    />
                  </Grid>
                </>
              )}
              {portalType === PortalType.Lepc && (
                <>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="medium">
                      <InputLabel id="portal-lepc-filter-state-label">State (filter)</InputLabel>
                      <Select
                        labelId="portal-lepc-filter-state-label"
                        value={lepcFilterState}
                        label="State (filter)"
                        onChange={(e) => {
                          setLepcFilterStateInput(e.target.value as string);
                          setSelectedLepcLocal(null);
                          setValue("jurisdiction", "", { shouldDirty: true });
                        }}
                      >
                        <MenuItem value="">
                          <em>Select state…</em>
                        </MenuItem>
                        {STATE_SELECT_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.display}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LepcPickerOmnisearch
                      defaultSearch={lepcFilterState ? `state:${lepcFilterState}` : ""}
                      resultFilter={(lepc) => !lepcFilterState || lepc.state === lepcFilterState}
                      value={selectedLepc}
                      onChange={handleLepcChange}
                      invalid={false}
                      isTouched={false}
                      isDirty={false}
                      isValidating={false}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DisplayOnlyField
                      label="Jurisdiction"
                      text={jurisdiction ?? ""}
                      infoTooltip="This is the actual value that we use to identify the portal in the system. Pattern is state_lepcId."
                    />
                  </Grid>
                </>
              )}
              {!isJurisdictionFromPicker && (
                <Grid item xs={12} md={12}>
                  <FormTextField
                    name="jurisdiction"
                    label="Jurisdiction"
                    control={control}
                    tooltip="Use state abbreviation or abbreviation_LEPCID for State/LEPC types; for City/Federal enter manually."
                  />
                </Grid>
              )}

              {/* Portal Tech (6) and URL (6) */}
              <Grid item xs={12} md={6}>
                <FormAutocomplete
                  name="portalTech"
                  label="Portal Tech"
                  control={control}
                  autocompleteItems={PORTAL_TECH_OPTIONS}
                  autocompleteProps={{ freeSolo: true }}
                />
              </Grid>

              {/* URL, Capacity, Chemical DB, Program Areas */}
              <Grid item xs={12} md={6}>
                <FormTextField
                  name="url"
                  label="URL"
                  control={control}
                  textFieldProps={{ type: "url" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormTextField
                  name="capacity"
                  label="Capacity"
                  control={control}
                  textFieldProps={{ type: "number", inputProps: { min: 1 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormSelect
                  name="requiresUsageOfChemicalDb"
                  label="Chemical DB Requirement"
                  control={control}
                  selectItems={CHEMICAL_DB_OPTIONS}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormSelect
                  name="supportedProgramAreas"
                  label="Supported Program Areas"
                  control={control}
                  selectItems={PROGRAM_AREA_OPTIONS}
                  selectProps={{ multiple: true }}
                />
              </Grid>

              {/* Login options */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Login options
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <ToggleField
                      name="allowsMultiSessionLogin"
                      label="Multi-Session Login"
                      tooltip="A single credential may log in multiple times at once"
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleField
                      name="allowsCookieReuse"
                      label="Reuses Cookies"
                      tooltip="Skip logout and then safely refresh session from a saved cookie"
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleField
                      name="usesMFA"
                      label="Multi-Factor Authentication"
                      tooltip="The login requires submitting a code from an email or other source"
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleField
                      name="verifiable"
                      label="Verifiable"
                      tooltip="This portal supports credential verification"
                      control={control}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}
      </Dialog>
    </FormProvider>
  );
};
